/**
 * AssignmentChannelGrid
 *
 * Left panel showing channel shelves as drop targets for image assignment.
 * Simplified version of DroppableChannelGrid for the assignment workflow.
 *
 * @accessibility WCAG 2.1 AA + BFSG konform
 */

import { useMemo } from 'react';
import { useDroppable } from '@dnd-kit/core';
import type { UniqueIdentifier } from '@dnd-kit/core';
import { Globe, Radio, Layers } from 'lucide-react';
import { Badge } from '@/components/v2/ui';
import { cn } from '@/lib/utils';
import type { ProductAsset } from '@/types/products';

interface PendingAssignment {
  assetId: string;
  channelId: string;
  channelKey: string;
}

interface AssignmentChannelGridProps {
  groupedByChannel: Record<string, ProductAsset[]>;
  pendingAssignments: PendingAssignment[];
  activeId: UniqueIdentifier | null;
}

export function AssignmentChannelGrid({
  groupedByChannel,
  pendingAssignments,
  activeId,
}: AssignmentChannelGridProps) {
  // Filter and sort channels (exclude internal channels)
  const sortedChannelKeys = useMemo(() => {
    return Object.keys(groupedByChannel)
      .filter((key) => {
        if (key === 'global') return true;
        const channelAssets = groupedByChannel[key];
        if (!channelAssets || channelAssets.length === 0) return false;
        const firstAsset = channelAssets[0];
        const channelType = typeof firstAsset.channel_type === 'string'
          ? firstAsset.channel_type.toLowerCase()
          : '';
        if (channelType.includes('wawi') || channelType === '1-1-1') return false;
        return true;
      })
      .sort((a, b) => {
        if (a === 'global') return -1;
        if (b === 'global') return 1;
        return a.localeCompare(b);
      });
  }, [groupedByChannel]);

  // Get pending assignments for a channel
  const getPendingForChannel = (channelKey: string) => {
    return pendingAssignments.filter(p => p.channelKey === channelKey);
  };

  if (sortedChannelKeys.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center">
        <Layers className="w-12 h-12 text-neutral-600 mb-4" />
        <h3 className="text-lg font-medium text-neutral-400">Keine Kanäle gefunden</h3>
        <p className="text-sm text-neutral-500 mt-1">
          Synchronisiere zuerst die Produktkanäle
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <div className="w-8 h-8 rounded-lg bg-neutral-800 flex items-center justify-center">
          <Layers className="w-4 h-4 text-neutral-400" />
        </div>
        <div>
          <h2 className="text-sm font-semibold text-white">Kanäle</h2>
          <p className="text-xs text-neutral-500">Bilder per Drag & Drop zuweisen</p>
        </div>
      </div>

      {/* Channel Shelves */}
      {sortedChannelKeys.map((channelKey, index) => {
        const channelAssets = groupedByChannel[channelKey] || [];
        const isGlobal = channelKey === 'global';
        const channelName = isGlobal
          ? 'Alle Kanäle'
          : channelAssets[0]?.channel_name || channelKey;
        const channelId = isGlobal ? null : channelAssets[0]?.channel_id || null;
        const pendingForChannel = getPendingForChannel(channelKey);

        return (
          <ChannelShelf
            key={channelKey}
            channelKey={channelKey}
            channelName={channelName}
            channelId={channelId}
            isGlobal={isGlobal}
            assets={channelAssets}
            pendingAssignments={pendingForChannel}
            activeId={activeId}
            animationDelay={index * 50}
          />
        );
      })}
    </div>
  );
}

// ============================================================================
// ChannelShelf Sub-Component
// ============================================================================

interface ChannelShelfProps {
  channelKey: string;
  channelName: string;
  channelId: string | null;
  isGlobal: boolean;
  assets: ProductAsset[];
  pendingAssignments: PendingAssignment[];
  activeId: UniqueIdentifier | null;
  animationDelay: number;
}

function ChannelShelf({
  channelKey,
  channelName,
  channelId,
  isGlobal,
  assets,
  pendingAssignments,
  activeId,
  animationDelay,
}: ChannelShelfProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: `channel-drop-${channelKey}`,
    data: {
      channelKey,
      channelId,
    },
    disabled: isGlobal, // Disable dropping on global for now
  });

  const isDragging = activeId !== null;
  const isDropTarget = isOver && !isGlobal;
  const hasPending = pendingAssignments.length > 0;

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'rounded-lg border overflow-hidden transition-all duration-200',
        'bg-gradient-to-b from-neutral-800/80 to-neutral-900/80',
        'animate-fade-in',
        isDropTarget
          ? 'border-primary-500 ring-2 ring-primary-500/30 shadow-lg shadow-primary-500/10'
          : isDragging && !isGlobal
          ? 'border-primary-500/30 border-dashed'
          : 'border-neutral-700/60'
      )}
      style={{ animationDelay: `${animationDelay}ms` }}
    >
      {/* Channel Header */}
      <div className={cn(
        'px-4 py-3 flex items-center gap-3',
        'bg-neutral-800/60 border-b border-neutral-700/40',
        !isGlobal && getChannelGradient(channelName)
      )}>
        {/* Icon */}
        <div className={cn(
          'flex items-center justify-center w-9 h-9 rounded-lg',
          isGlobal
            ? 'bg-neutral-700/50'
            : isDropTarget
            ? 'bg-primary-600/30'
            : 'bg-primary-900/30 border border-primary-800/30'
        )}>
          {isGlobal ? (
            <Globe className="w-5 h-5 text-neutral-400" />
          ) : (
            <Radio className={cn(
              'w-5 h-5',
              isDropTarget ? 'text-primary-400' : 'text-primary-500'
            )} />
          )}
        </div>

        {/* Info */}
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-white">{channelName}</h3>
            {isGlobal && (
              <Badge variant="secondary" className="text-2xs">Global</Badge>
            )}
            {isDropTarget && (
              <Badge variant="info" className="text-2xs animate-pulse">
                Hier ablegen
              </Badge>
            )}
            {hasPending && !isDropTarget && (
              <Badge variant="success" className="text-2xs">
                +{pendingAssignments.length} neu
              </Badge>
            )}
          </div>
          <p className="text-xs text-neutral-500">
            {assets.length} Bild{assets.length !== 1 ? 'er' : ''}
            {hasPending && ` + ${pendingAssignments.length} ausstehend`}
          </p>
        </div>
      </div>

      {/* Images Grid - 150px max per image */}
      <div className="p-3">
        <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 150px))' }}>
          {/* Existing Assets */}
          {assets.slice(0, 6).map((asset, index) => (
            <div
              key={asset.link_id}
              className="relative aspect-square rounded-lg border border-neutral-700 overflow-hidden bg-neutral-800"
              style={{ maxWidth: 150 }}
            >
              <img
                src={`/api/v1/asset/${asset.asset_id}/image`}
                alt={asset.alt_text || `Bild ${index + 1}`}
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <span className="absolute top-1 right-1 px-1.5 py-0.5 text-3xs font-bold rounded bg-primary-600 text-white">
                #{index + 1}
              </span>
            </div>
          ))}

          {/* Pending Assignments (virtual) - use /image/{id} for generated images */}
          {pendingAssignments.map((pending, index) => (
            <div
              key={`pending-${pending.assetId}`}
              className="relative aspect-square rounded-lg border-2 border-dashed border-emerald-500/60 overflow-hidden bg-neutral-800"
              style={{ maxWidth: 150 }}
            >
              <img
                src={`/image/${pending.assetId}`}
                alt={`Neu ${index + 1}`}
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <span className="absolute -top-1 -right-1 px-1.5 py-0.5 text-3xs font-bold rounded-full bg-gradient-to-br from-amber-400 to-orange-500 text-neutral-900 shadow-lg z-10">
                Neu
              </span>
              <span className="absolute top-5 right-1 px-1.5 py-0.5 text-3xs font-bold rounded bg-primary-600/90 text-white">
                #{assets.length + index + 1}
              </span>
            </div>
          ))}

          {/* Drop Zone Slot */}
          {!isGlobal && (
            <div
              className={cn(
                'relative aspect-square rounded-lg border-2 border-dashed flex items-center justify-center transition-all',
                isDropTarget
                  ? 'border-primary-500 bg-primary-500/10'
                  : 'border-neutral-600 bg-neutral-800/30 hover:border-primary-500/50'
              )}
              style={{ maxWidth: 150 }}
            >
              {isDropTarget ? (
                <div className="text-center">
                  <svg className="w-5 h-5 mx-auto text-primary-400 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                  <span className="text-3xs text-primary-400 font-medium">Drop</span>
                </div>
              ) : (
                <div className="w-6 h-6 rounded-full bg-neutral-700/50 flex items-center justify-center">
                  <svg className="w-3 h-3 text-neutral-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Helper: Get channel-specific gradient
function getChannelGradient(channelName: string): string {
  const name = channelName.toLowerCase();
  if (name.includes('amazon')) {
    return 'bg-gradient-to-r from-orange-900/20 to-transparent';
  }
  if (name.includes('ebay')) {
    return 'bg-gradient-to-r from-primary-900/15 to-transparent';
  }
  if (name.includes('otto')) {
    return 'bg-gradient-to-r from-red-900/15 to-transparent';
  }
  if (name.includes('shopify') || name.includes('shop')) {
    return 'bg-gradient-to-r from-green-900/15 to-transparent';
  }
  return '';
}
